import { useEffect, useState } from 'react';
import type { IChartApi } from 'lightweight-charts';
import { clampTimeScale, stepPosition } from './nav';

/**
 * 主图左下角缩放/平移按钮。
 *
 * lightweight-charts **不提供任何内置导航 UI**，而默认的滚轮缩放/拖拽平移
 * 在触屏上不易发现且手势冲突，故自建四个按钮，移动端同样可用。
 *
 * 平移走 `scrollPosition()`（右边缘到最新柱的根数，实时位置 = `rightOffset`）：
* dir=-1 左移看更早的数据、dir=+1 右移回到更新，每次恰好 1 根，
 * 两侧边界由 nav.ts 统一判定（首根柱贴左缘、末柱停在实时位置）。
 */
export function PriceScaleControls({ chart }: { chart: IChartApi | null }) {
  /**
   * 主图 pane 的真实高度 —— 控件要贴在**主图内侧**左下角，而不是整个图底部
   * （整个图底部落在副图 pane 区域内，VOL 等指标图会被压住）。
   *
   * 不用 lib/layout 的 solveChartSpace().mainHeight：那个模型预测 338px 而
   * 实际渲染 327px（库自行分配 pane 高度），差 11px 足以让控件明显偏位。
   * 这里直接问库要真实值，并订阅尺寸变化跟随重绘。
   */
  const [mainH, setMainH] = useState(0);

  useEffect(() => {
    if (!chart) {
      setMainH(0);
      return;
    }
    const read = () => setMainH(chart.panes()[0]?.getHeight() ?? 0);
    read();
    const ts = chart.timeScale();
    ts.subscribeSizeChange(read);
    return () => ts.unsubscribeSizeChange(read);
  }, [chart]);

  if (!chart) return null;
  const ts = chart.timeScale();

  const zoom = (factor: number) => {
    const opt = ts.options();
    // 下限 0.5：再小单根柱宽度不足 1px，图表会拒绝或退化
    const next = Math.min(400, Math.max(0.5, opt.barSpacing * factor));
    ts.applyOptions({ barSpacing: next });
  };

  const pan = (dir: -1 | 1) => {
    // scrollPosition 减小 = 看更早的数据（左移），增大 = 回到更新（右移）
    ts.scrollToPosition(stepPosition(ts.scrollPosition(), dir, ts.options().rightOffset), false);
    // 同步纠偏，避免左移到头时先越界再弹回的闪动
    clampTimeScale(ts);
  };

  const reset = () => {
    ts.scrollToRealTime();
  };

  const btn = 'grid h-5 w-5 place-items-center rounded text-[11px] transition-colors hover:bg-[var(--bg-hover)] disabled:opacity-30';
  /** 控件组实测高 22px（按钮 20 + 边框），再加 4px 间距贴住 pane 下沿 */
  const CTRL_H = 26;

  return (
    <div
      className="absolute left-1 z-20 flex items-center gap-0.5 rounded border px-0.5"
      style={{
        // mainH 为 0（图表尚未完成首次布局）时回退到底部，避免悬空
        top: mainH > 0 ? mainH - CTRL_H : undefined,
        bottom: mainH > 0 ? undefined : 4,
        background: 'var(--bg-surface)',
        borderColor: 'var(--border)',
      }}
      role="group"
      aria-label="图表缩放与平移"
      // 拖动图表时不触发这些按钮
      onPointerDown={(e) => e.stopPropagation()}
      // 双击按钮不应冒泡到图表绘图区的「换高度档」手势（pointer 事件拦不住 dblclick）
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <button type="button" className={btn} onClick={() => zoom(1 / 1.25)} title="缩小" aria-label="缩小">
        −
      </button>
      <button type="button" className={btn} onClick={() => zoom(1.25)} title="放大" aria-label="放大">
        ＋
      </button>
      <span className="mx-0.5 h-3 w-px" style={{ background: 'var(--border)' }} />
      <button type="button" className={btn} onClick={() => pan(-1)} title="左移" aria-label="左移">
        ‹
      </button>
      <button type="button" className={btn} onClick={() => pan(1)} title="右移" aria-label="右移">
        ›
      </button>
      <button
        type="button"
        className={btn}
        onClick={reset}
        title="回到最新"
        aria-label="回到最新"
        style={{ fontSize: 10 }}
      >
        ⟲
      </button>
    </div>
  );
}